import { create } from 'zustand';
import type { MerchSaleLine, RebookOutcome } from '@/domain/types';

/** Floor make-sale wizard steps (moved here so the draft can carry the step). */
export type Step =
  | 'overview'
  | 'checked-in'
  | 'in-chair'
  | 'progress'
  | 'rebook-outcome'
  | 'payment'
  | 'done';

/** Make-sale detail captured on the payment step. */
export interface SaleState {
  /** null === use the rebook outcome default (roll -> 0, apply -> full deposit). */
  depositApplied: number | null;
  depositReason: string;
  merchLines: MerchSaleLine[];
  cashTendered: number;
  /** Reasoned +/- charge adjustment (D3). Signed: negative reduces, positive adds. */
  adjustment: { amountAud: number; reason: string } | null;
}

/**
 * A snapshot of the in-progress make-sale, held so the manager can leave for the
 * calendar to pick a custom rebook date and return without losing anything.
 */
export interface MakeSaleDraft {
  step: Step;
  outcome: RebookOutcome['kind'];
  sale: SaleState;
  sessionNote: string;
  futurePhrase: string;
  snappedPhotos: string[];
  hoursToAdd: number;
  markComplete: boolean;
  paymentMethod: 'card' | 'cash' | 'mixed';
  /** Written by the calendar when the manager picks a custom slot. */
  customSlotIso?: string;
  customSlotArtistId?: string;
}

interface MakeSaleDraftStore {
  drafts: Record<string, MakeSaleDraft>;
  saveDraft: (bookingId: string, draft: MakeSaleDraft) => void;
  setPickedSlot: (bookingId: string, iso: string, artistId: string) => void;
  clearDraft: (bookingId: string) => void;
}

export const useMakeSaleDraftStore = create<MakeSaleDraftStore>((set) => ({
  drafts: {},
  saveDraft: (bookingId, draft) =>
    set((s) => ({ drafts: { ...s.drafts, [bookingId]: draft } })),
  setPickedSlot: (bookingId, iso, artistId) =>
    set((s) => {
      const existing = s.drafts[bookingId];
      if (!existing) return s;
      return {
        drafts: {
          ...s.drafts,
          [bookingId]: { ...existing, customSlotIso: iso, customSlotArtistId: artistId },
        },
      };
    }),
  clearDraft: (bookingId) =>
    set((s) => {
      const next = { ...s.drafts };
      delete next[bookingId];
      return { drafts: next };
    }),
}));
